import mixpanel from 'mixpanel-browser'
import { getEnv } from '@/config/get-env'
import type {
  MixpanelEvent,
  MixpanelEventProps,
} from '@/components/mixpanel/types'

const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined

const isProduction = getEnv() === 'production'

const COMPANY_GROUP_KEY = 'company_id'

let isInitialized = false

// When true, track() is a no-op. Used to keep Kinsta employees' actions out of
// product metrics while still identifying them (see MixpanelIdentifier).
let isEventTrackingSuppressed = false

const logError = (error: unknown) => {
  if (!isProduction) {
    console.warn('[mixpanel]', error)
  }
}

export const initMixpanel = () => {
  if (!token || isInitialized) {
    return
  }

  try {
    mixpanel.init(token, {
      // Project is on Mixpanel EU data residency (eu.mixpanel.com) — route
      // ingestion to the EU host, otherwise events go to the US default and
      // never reach the project.
      api_host: 'https://api-eu.mixpanel.com',
      autocapture: false,
      track_pageview: false,
      record_sessions_percent: 0,
      persistence: 'localStorage',
      debug: !isProduction,
    })
    isInitialized = true
  } catch (error) {
    logError(error)
  }
}

export const setEventTrackingSuppressed = (suppressed: boolean) => {
  isEventTrackingSuppressed = suppressed
}

export const trackEvent = (
  event: MixpanelEvent,
  props?: MixpanelEventProps
) => {
  if (!isInitialized || isEventTrackingSuppressed) {
    return
  }

  try {
    mixpanel.track(event, props)
  } catch (error) {
    logError(error)
  }
}

export const identifyUser = (idUser: string) => {
  if (!isInitialized) {
    return
  }

  try {
    mixpanel.identify(idUser)
  } catch (error) {
    logError(error)
  }
}

export const registerSuperProperties = (props: MixpanelEventProps) => {
  if (!isInitialized) {
    return
  }

  try {
    mixpanel.register(props)
  } catch (error) {
    logError(error)
  }
}

export const setUserProfileProperties = (props: MixpanelEventProps) => {
  if (!isInitialized) {
    return
  }

  try {
    mixpanel.people.set(props)
  } catch (error) {
    logError(error)
  }
}

export const identifyCompany = ({
  idCompany,
  name,
}: {
  idCompany: string
  name: string
}) => {
  if (!isInitialized) {
    return
  }

  try {
    // Associates the current user with the company group (also manages the
    // `company_id` super property), then sets properties on the group profile.
    mixpanel.set_group(COMPANY_GROUP_KEY, idCompany)
    mixpanel.get_group(COMPANY_GROUP_KEY, idCompany).set({ $name: name })
  } catch (error) {
    logError(error)
  }
}

export const resetMixpanel = () => {
  if (!isInitialized) {
    return
  }

  try {
    mixpanel.reset()
  } catch (error) {
    logError(error)
  }
}
